import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import moment from 'moment';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import { truncateText } from '../utils/Parser';
import Pagination from '../components/Pagination';
import { HeaderHeight, PageHeaderHeight } from '../Constants';
import Header from '../components/navbar/Header';
import Page from '../components/Page';
import { GetAllTicket } from '../network/TicketManagement';
import UpdateTicketStatus from '../components/_dashboard/smartlock/UpdateTicketStatus';
import NoDataAvailable from '../components/_dashboard/smartlock/NoDataAvailable';

const paginationModel = { page: 0, pageSize: 5 };

const LoaderContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '50px'
});

const PageHeader = styled('Box')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 1,
  flexDirection: 'row',
  height: PageHeaderHeight,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column'
  }
}));

const useStyles = makeStyles((theme) => ({
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  },
  tableCellStyle: {
    fontSize: {
      xs: '12px',
      md: '14px'
    },
    fontWeight: 700,
    padding: {
      xs: '8px',
      md: '16px'
    },
    textWrap: 'nowrap',
    textTransform: 'capitalize',
    backgroundColor: '#FFFFFF'
  },
  tablePaper: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF'
  },
  tableContainer: {
    flexGrow: 1,
    height: `calc(100vh - ${HeaderHeight + PageHeaderHeight + 56 + 40 + 50}px)`,
    overflow: 'auto'
  }
}));

export default function TicketManagement() {
  const classes = useStyles();
  const [ticketData, setTicketData] = useState([]);
  const [page, setPage] = useState(0);
  const [filterBy, setFilterBy] = useState('');
  const [loader, setLoader] = useState(false);
  const [dataPerPage, setDataPerPage] = useState(10); // Default rows per page
  const [pageCount, setPageCount] = useState(0);
  const [updateTicketStatusModal, setUpdateTicketStatusModal] = useState(false);
  const [ticketSelectedData, setTicketSelectedData] = useState(null);
  const [alertMetas, setAlertMetas] = useState({
    snackBarOpened: false,
    vertical: 'top',
    horizontal: 'right',
    msg: '',
    variant: 'filled',
    severity: ''
  });

  const vertical = 'bottom';
  const horizontal = 'left';

  // const handleSnackBarClose = (event, reason) => {
  //   if (reason === 'clickaway') {
  //     return;
  //   }
  //   setAlertMetas({ ...alertMetas, snackBarOpened: false });
  // };

  const getTickets = async (page, dataPerPage, filterBy) => {
    setLoader(true);
    try {
      const res = await GetAllTicket(page + 1, dataPerPage, filterBy);
      if (res) {
        setLoader(false);
        setTicketData(res?.data?.data || []);
        setPageCount(res?.data?.total);
      }
    } catch (error) {
      setLoader(false);
      console.log(error, 'error');
    }
  };

  const handleFilter = (e) => {
    setFilterBy(e.target.value);
    setPage(0);
  };

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
  };

  const handleDataPerPage = (e) => {
    setDataPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    getTickets(page, dataPerPage, filterBy);
  }, [page, dataPerPage, filterBy]);

  return (
    <>
      <Page title="Ticket Management">
        <Header
          // showSearch
          name="Ticket Management"
        />
        <Snackbar
          anchorOrigin={{ vertical, horizontal }}
          open={alertMetas?.snackBarOpened}
          // onClose={handleSnackBarClose}
          autoHideDuration={6000}
        >
          <Alert variant={alertMetas?.variant} severity={alertMetas?.severity}>
            {alertMetas?.msg}
          </Alert>
        </Snackbar>

        <Container maxWidth="false" disableGutters sx={{ padding: 0 }}>
          <Card sx={{ p: 2 }}>
            <PageHeader sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="start">
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Filter By</Typography>
                <FormControl data-testid="TicketFilterBy" size="small" sx={{ minWidth: 150 }}>
                  <Select
                    labelId="filter-by"
                    id="filter-by"
                    value={filterBy}
                    onChange={handleFilter}
                    IconComponent={ExpandMoreIcon}
                    sx={{
                      fontSize: { xs: '12px', sm: '14px' },
                      fontWeight: '500',
                      minWidth: '130px',
                      borderRadius: '12px'
                    }}
                  >
                    <MenuItem className={classes.menuItem}>All</MenuItem>
                    <MenuItem className={classes.menuItem} value="true">
                      Active
                    </MenuItem>
                    <MenuItem className={classes.menuItem} value="false">
                      Resolved
                    </MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </PageHeader>
            <Divider sx={{ m: 1 }} />
            <Box sx={{ width: '100%', typography: 'body1' }}>
              <Paper className={classes.tablePaper}>
                <TableContainer className={classes.tableContainer}>
                  <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          className={classes.tableCellStyle}
                          sx={{
                            cursor: 'pointer'
                          }}
                          width={100}
                          align="left"
                        >
                          Ticket ID
                        </TableCell>
                        <TableCell className={classes.tableCellStyle} width={150}>
                          Complained By
                        </TableCell>
                        <TableCell className={classes.tableCellStyle} width={150}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              fontWeight: 800
                            }}
                          >
                            Date
                            <CalendarTodayOutlinedIcon
                              sx={{ fontSize: '16px', color: '#000000' }}
                            />
                          </div>
                        </TableCell>
                        <TableCell className={classes.tableCellStyle} width={240}>
                          Subject
                        </TableCell>
                        <TableCell className={classes.tableCellStyle} width={120}>
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {ticketData?.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10}>
                            <NoDataAvailable />
                          </TableCell>
                        </TableRow>
                      )}
                      {loader ? (
                        <TableRow>
                          <TableCell colSpan={10}>
                            <LoaderContainer>
                              <CircularProgress size={30} />
                            </LoaderContainer>
                          </TableCell>
                        </TableRow>
                      ) : (
                        ticketData?.map((item) => (
                          <TableRow
                            key={item?.id}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell
                              component="th"
                              scope="row"
                              align="left"
                              sx={{
                                minWidth: '5vw',
                                textWrap: 'nowrap',
                                textTransform: 'capitalize',
                                padding: {
                                  xs: '8px',
                                  md: '16px'
                                },
                                fontSize: {
                                  xs: '12px',
                                  md: '14px'
                                }
                              }}
                            >
                              <Box
                                onClick={() => {
                                  setTicketSelectedData(item);
                                  setUpdateTicketStatusModal(true);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  textDecoration: 'underline'
                                  // color: '#0000EE'
                                }}
                              >
                                {item?.id}
                              </Box>
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              align="left"
                              sx={{
                                minWidth: '5vw',
                                textWrap: 'nowrap',
                                textTransform: 'capitalize',
                                padding: {
                                  xs: '8px',
                                  md: '16px'
                                },
                                fontSize: {
                                  xs: '12px',
                                  md: '14px' // Larger screens
                                }
                              }}
                            >
                              {item?.name}
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              align="left"
                              sx={{
                                minWidth: '5vw',
                                textWrap: 'nowrap',
                                textTransform: 'capitalize',
                                padding: {
                                  xs: '8px',
                                  md: '16px'
                                },
                                fontSize: {
                                  xs: '12px',
                                  md: '14px' // Larger screens
                                }
                              }}
                            >
                              {moment(item?.createdAt)?.format('DD/MM/YYYY')}
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              align="left"
                              sx={{
                                minWidth: '5vw',
                                textWrap: 'nowrap',
                                textTransform: 'capitalize',
                                cursor: 'pointer',
                                padding: {
                                  xs: '8px',
                                  md: '16px'
                                },
                                fontSize: {
                                  xs: '12px',
                                  md: '14px' // Larger screens
                                }
                              }}
                            >
                              <Tooltip
                                sx={{ textTransform: 'capitalize' }}
                                placement="top"
                                title={item?.title}
                              >
                                {item?.title !== undefined ? truncateText(item?.title, 30) : '---'}
                              </Tooltip>
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              align="left"
                              sx={{
                                minWidth: '5vw',
                                textWrap: 'nowrap',
                                textTransform: 'capitalize',
                                padding: {
                                  xs: '8px',
                                  md: '16px'
                                },
                                fontSize: {
                                  xs: '12px',
                                  md: '14px' // Larger screens
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                gap={1}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: item?.isActive ? '#FF9900' : '#0DA626'
                                  }}
                                />
                                {item?.isActive}
                                <Typography sx={{ fontSize: { xs: '12px', md: '14px' } }}>
                                  {item?.isActive ? 'Active' : 'Resolved'}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>

            <Pagination
              pageNumber={page}
              limit={dataPerPage}
              handleChangePage={handlePageChange}
              handleChangeRowsPerPage={handleDataPerPage}
              pageCount={pageCount}
              sx={{
                '.MuiPagination-ul': {
                  fontSize: '12px'
                }
              }}
            />
          </Card>
        </Container>
      </Page>
      {updateTicketStatusModal && (
        <UpdateTicketStatus
          openAssignedModal={updateTicketStatusModal}
          handleClose={() => setUpdateTicketStatusModal(false)}
          data={ticketSelectedData}
          alertMetas={alertMetas}
          setAlertMetas={setAlertMetas}
          onAction={(d) => getTickets(d)}
        />
      )}
    </>
  );
}
